/**
 * Server-side fetch utility with Origin header
 * Use this for all server-side API calls to ensure Cloudflare Worker bypass works
 * 
 * This is optimized for Vercel Edge Functions and Serverless Functions
 */

export async function serverFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // Get origin from environment variable (works on Vercel)
  const origin = process.env.NEXT_PUBLIC_API_BASE_URL || 
                 (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : 'http://localhost:3000');
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 [serverFetch] NEXT_PUBLIC_API_BASE_URL:', process.env.NEXT_PUBLIC_API_BASE_URL);
    console.log('🔧 [serverFetch] VERCEL_URL:', process.env.VERCEL_URL);
    console.log('🔧 [serverFetch] Computed origin:', origin);
  }
  
  // Build headers with Origin
  const headers = new Headers(options.headers || {});
  
  // Set required headers for server-side requests
  if (!headers.has('Origin')) {
    headers.set('Origin', origin);
  }
  headers.set('X-Next-Server', 'true');
  headers.set('User-Agent', 'NextJS-SSR/15.0');
  
  try {
    headers.set('X-Forwarded-Host', new URL(origin).hostname);
  } catch (e) {
    // Fallback if URL parsing fails
    headers.set('X-Forwarded-Host', 'localhost');
  }
  
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  console.log('🌐 [serverFetch] Origin:', origin, '→', url);
  console.log('📋 [serverFetch] Headers:', Object.fromEntries(headers.entries()));
  
  // Perform fetch with modified headers
  const response = await fetch(url, {
    ...options,
    headers,
  });
  
  console.log('📥 [serverFetch] Response:', response.status, response.statusText);
  
  return response;
}

/**
 * Convenience wrapper for GET requests
 */
export async function serverGet<T = any>(url: string, options: Omit<RequestInit, 'method'> = {}): Promise<T> {
  const response = await serverFetch(url, { ...options, method: 'GET' });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Convenience wrapper for POST requests
 */
export async function serverPost<T = any>(url: string, body?: any, options: Omit<RequestInit, 'method' | 'body'> = {}): Promise<T> {
  const response = await serverFetch(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}
